import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Calculator, Send, Check, FileText } from "lucide-react";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { PublicHeader } from "../components/PublicHeader";
import { PublicFooter } from "../components/PublicFooter";

const yen = (n) => `¥${Number(n || 0).toLocaleString()}`;

const computeQuote = (plan, settings, employeeCount, billingCycle, options) => {
  const yearly = billingCycle === "yearly";
  const taxRate = settings.tax_rate || 0;
  const billingLabel = yearly ? "年額" : "月額";
  const recurring = [];
  const onetime = [];

  const base = yearly ? plan.price_yearly || 0 : plan.price_monthly || 0;
  recurring.push({ label: `${plan.name}プラン（${billingLabel}）`, qty: 1, unit_price: base, amount: base });

  const maxEmp = plan.max_employees || 0;
  const over = maxEmp ? Math.max(0, employeeCount - maxEmp) : 0;
  const perFee = settings.per_employee_fee || 0;
  if (over > 0 && perFee > 0) {
    const unit = yearly ? perFee * 12 : perFee;
    recurring.push({ label: `追加従業員（${maxEmp}名超過分・${billingLabel}）`, qty: over, unit_price: unit, amount: unit * over });
  }

  const optMap = {};
  (settings.options || []).forEach((o) => {
    optMap[o.id] = o;
  });
  (options || []).forEach((sel) => {
    const o = optMap[sel.id];
    if (!o) return;
    const qty = Math.max(1, parseInt(sel.qty) || 1);
    const price = o.price || 0;
    if (o.billing === "onetime") {
      onetime.push({ label: o.name, qty, unit_price: price, amount: price * qty });
    } else {
      const unit = yearly ? price * 12 : price;
      recurring.push({ label: `${o.name}（${billingLabel}）`, qty, unit_price: unit, amount: unit * qty });
    }
  });

  const initFee = settings.initial_fee || 0;
  if (initFee > 0) onetime.unshift({ label: "初期導入費用", qty: 1, unit_price: initFee, amount: initFee });

  const rSub = recurring.reduce((s, i) => s + i.amount, 0);
  const oSub = onetime.reduce((s, i) => s + i.amount, 0);
  const rTax = Math.round((rSub * taxRate) / 100);
  const oTax = Math.round((oSub * taxRate) / 100);
  return {
    billingLabel,
    recurring,
    onetime,
    rSub,
    oSub,
    taxRate,
    rTax,
    oTax,
    rTotal: rSub + rTax,
    oTotal: oSub + oTax,
    initialPayment: rSub + rTax + oSub + oTax,
  };
};

const LineTable = ({ title, items, sub, tax, total, taxRate, totalLabel }) => (
  <div className="mb-5" data-testid={`line-table-${totalLabel}`}>
    <p className="font-semibold text-sm mb-2 text-slate-700 dark:text-slate-200">{title}</p>
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden text-sm">
      {items.map((it, i) => (
        <div key={i} className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100 dark:border-slate-800">
          <span className="text-slate-600 dark:text-slate-300">
            {it.label}
            {it.qty > 1 ? <span className="text-slate-400 ml-1">× {it.qty}</span> : null}
          </span>
          <span className="tabular-nums font-medium">{yen(it.amount)}</span>
        </div>
      ))}
      <div className="flex items-center justify-between px-4 py-2 text-slate-500">
        <span>小計</span>
        <span className="tabular-nums">{yen(sub)}</span>
      </div>
      <div className="flex items-center justify-between px-4 py-2 text-slate-500">
        <span>消費税（{taxRate}%）</span>
        <span className="tabular-nums">{yen(tax)}</span>
      </div>
      <div className="flex items-center justify-between px-4 py-3 bg-slate-50 dark:bg-slate-900 font-bold">
        <span>{totalLabel}合計（税込）</span>
        <span className="tabular-nums text-emerald-600">{yen(total)}</span>
      </div>
    </div>
  </div>
);

export default function QuoteSimulatorPage() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [planId, setPlanId] = useState("");
  const [employeeCount, setEmployeeCount] = useState(10);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [optionIds, setOptionIds] = useState([]);
  const [optionQtys, setOptionQtys] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios
      .get(`${API}/site/quote-config`)
      .then((res) => {
        setConfig(res.data);
        const plans = res.data.plans || [];
        const paid = plans.find((p) => (p.price_monthly || 0) > 0) || plans[0];
        if (paid) setPlanId(paid.id);
      })
      .catch(() => toast.error("料金情報の取得に失敗しました"))
      .finally(() => setLoading(false));
  }, []);

  const plan = useMemo(() => (config?.plans || []).find((p) => p.id === planId), [config, planId]);
  const selectedOptions = useMemo(
    () => optionIds.map((id) => ({ id, qty: Number(optionQtys[id]) || 1 })),
    [optionIds, optionQtys]
  );
  const estimate = useMemo(() => {
    if (!plan || !config) return null;
    return computeQuote(plan, config.settings, Number(employeeCount) || 1, billingCycle, selectedOptions);
  }, [plan, config, employeeCount, billingCycle, selectedOptions]);

  const toggleOption = (id) => {
    if (optionIds.includes(id)) {
      setOptionIds((prev) => prev.filter((x) => x !== id));
      setOptionQtys((q) => {
        const n = { ...q };
        delete n[id];
        return n;
      });
    } else {
      setOptionIds((prev) => [...prev, id]);
      setOptionQtys((q) => ({ ...q, [id]: q[id] || 1 }));
    }
  };

  const setOptionQty = (id, val) => setOptionQtys((q) => ({ ...q, [id]: val }));

  const submitRequest = async () => {
    if (!form.name || !form.email) {
      toast.error("お名前・メールアドレスは必須です");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/site/quote`, {
        ...form,
        employee_count: Number(employeeCount) || 1,
        plan_id: planId,
        billing_cycle: billingCycle,
        options: selectedOptions,
      });
      setResult(res.data);
      toast.success("見積依頼を受け付けました");
    } catch (e) {
      toast.error(e.response?.data?.detail || "送信に失敗しました");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col">
      <PublicHeader />
      <main className="flex-1">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="flex items-center gap-3 mb-2">
            <Calculator className="w-7 h-7 text-emerald-500" />
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" data-testid="quote-title">
              導入費用シミュレーター
            </h1>
          </div>
          <p className="text-slate-500 dark:text-slate-400 mb-10">
            従業員数・プラン・オプションを選ぶと、導入にかかる概算費用がその場で分かります。
          </p>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
            </div>
          ) : !config ? (
            <p className="text-slate-500">料金情報を読み込めませんでした。</p>
          ) : result ? (
            <div className="max-w-xl mx-auto text-center py-16" data-testid="quote-success">
              <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center mx-auto mb-6">
                <Check className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold mb-2">見積依頼を受け付けました</h2>
              <p className="text-slate-500 dark:text-slate-400 mb-6">
                見積番号 <span className="font-mono font-semibold text-emerald-600">{result.quote_number}</span> で承りました。
                <br />
                担当者より正式なお見積書をお送りいたします。
              </p>
              <div className="inline-block text-left rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
                <p className="text-sm text-slate-500 mb-1">初回お支払い概算（税込）</p>
                <p className="text-3xl font-bold text-emerald-600 tabular-nums">{yen(result.estimate.initial_payment_total)}</p>
              </div>
            </div>
          ) : (
            <div className="grid lg:grid-cols-5 gap-8">
              {/* 入力 */}
              <div className="lg:col-span-3 space-y-8">
                {/* プラン */}
                <div>
                  <p className="font-semibold mb-3">1. プランを選択</p>
                  <div className="grid sm:grid-cols-3 gap-3">
                    {(config.plans || []).map((p) => {
                      const active = p.id === planId;
                      return (
                        <button
                          key={p.id}
                          onClick={() => setPlanId(p.id)}
                          data-testid={`quote-plan-${p.name}`}
                          className={`text-left p-4 rounded-2xl border-2 transition-all ${
                            active
                              ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20"
                              : "border-slate-200 dark:border-slate-800 hover:border-emerald-300"
                          }`}
                        >
                          <p className="font-bold">{p.name}</p>
                          <p className="text-xs text-slate-500 mb-1">〜{p.max_employees}名</p>
                          <p className="text-sm font-semibold text-emerald-600">
                            {p.price_monthly ? `${yen(p.price_monthly)}/月` : "無料"}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* 課金サイクル */}
                <div>
                  <p className="font-semibold mb-3">2. お支払いサイクル</p>
                  <div className="inline-flex rounded-xl border border-slate-200 dark:border-slate-800 p-1 bg-white dark:bg-slate-900">
                    {[
                      ["monthly", "月額"],
                      ["yearly", "年額（お得）"],
                    ].map(([v, label]) => (
                      <button
                        key={v}
                        onClick={() => setBillingCycle(v)}
                        data-testid={`quote-billing-${v}`}
                        className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${
                          billingCycle === v ? "bg-emerald-600 text-white" : "text-slate-600 dark:text-slate-300"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* 従業員数 */}
                <div>
                  <p className="font-semibold mb-3">3. 従業員数</p>
                  <div className="flex items-center gap-3">
                    <Input
                      type="number"
                      min={1}
                      value={employeeCount}
                      onChange={(e) => setEmployeeCount(e.target.value)}
                      data-testid="quote-employee-count"
                      className="w-40"
                    />
                    <span className="text-slate-500">名</span>
                  </div>
                  {plan && plan.max_employees > 0 && Number(employeeCount) > plan.max_employees && config.settings.per_employee_fee > 0 && (
                    <p className="text-xs text-amber-600 mt-2">
                      ※ {plan.max_employees}名を超える分は1名あたり{yen(config.settings.per_employee_fee)}/月の追加料金がかかります。
                    </p>
                  )}
                </div>

                {/* オプション */}
                {(config.settings.options || []).length > 0 && (
                  <div>
                    <p className="font-semibold mb-3">4. オプション（任意）</p>
                    <div className="space-y-2">
                      {config.settings.options.map((o) => {
                        const active = optionIds.includes(o.id);
                        const q = Number(optionQtys[o.id]) || 1;
                        const lineAmt = (o.price || 0) * q * (o.billing !== "onetime" && billingCycle === "yearly" ? 12 : 1);
                        return (
                          <div
                            key={o.id}
                            className={`rounded-xl border transition-all ${
                              active
                                ? "border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20"
                                : "border-slate-200 dark:border-slate-800 hover:border-emerald-300"
                            }`}
                          >
                            <button
                              onClick={() => toggleOption(o.id)}
                              data-testid={`quote-option-${o.id}`}
                              className="w-full flex items-center gap-3 text-left p-3"
                            >
                              <span
                                className={`w-5 h-5 rounded flex items-center justify-center border ${
                                  active ? "bg-emerald-600 border-emerald-600" : "border-slate-300"
                                }`}
                              >
                                {active && <Check className="w-3.5 h-3.5 text-white" />}
                              </span>
                              <span className="flex-1">
                                <span className="font-medium text-sm">{o.name}</span>
                                {o.description && <span className="block text-xs text-slate-500">{o.description}</span>}
                              </span>
                              <span className="text-sm font-semibold text-emerald-600 whitespace-nowrap">
                                {yen(o.price)}
                                {o.billing === "onetime" ? "（初期）" : "/月"}
                              </span>
                            </button>
                            {active && (
                              <div className="flex items-center gap-2 px-3 pb-3 pl-11">
                                <span className="text-xs text-slate-500">数量</span>
                                <input
                                  type="number"
                                  min={1}
                                  value={optionQtys[o.id] ?? 1}
                                  onChange={(e) => setOptionQty(o.id, e.target.value)}
                                  data-testid={`quote-option-qty-${o.id}`}
                                  className="w-24 h-9 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 text-sm text-center"
                                />
                                <span className="text-xs text-slate-500 ml-auto">
                                  = {yen(lineAmt)}
                                  {o.billing === "onetime" ? "（初期）" : billingCycle === "yearly" ? "/年" : "/月"}
                                </span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* 結果 */}
              <div className="lg:col-span-2">
                <div className="sticky top-24 rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6" data-testid="quote-result">
                  <p className="text-sm text-slate-500 mb-1">初回お支払い概算（税込）</p>
                  <p className="text-4xl font-bold text-emerald-600 tabular-nums mb-6" data-testid="quote-total">
                    {estimate ? yen(estimate.initialPayment) : "—"}
                  </p>

                  {estimate && (
                    <>
                      {estimate.onetime.length > 0 && (
                        <LineTable
                          title="初期費用（初回のみ）"
                          items={estimate.onetime}
                          sub={estimate.oSub}
                          tax={estimate.oTax}
                          total={estimate.oTotal}
                          taxRate={estimate.taxRate}
                          totalLabel="初期費用"
                        />
                      )}
                      <LineTable
                        title={`継続費用（${estimate.billingLabel}）`}
                        items={estimate.recurring}
                        sub={estimate.rSub}
                        tax={estimate.rTax}
                        total={estimate.rTotal}
                        taxRate={estimate.taxRate}
                        totalLabel={estimate.billingLabel}
                      />
                    </>
                  )}

                  {!showForm ? (
                    <Button
                      onClick={() => setShowForm(true)}
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                      data-testid="quote-request-btn"
                    >
                      <FileText className="w-4 h-4 mr-2" />
                      この内容で見積を依頼する
                    </Button>
                  ) : (
                    <div className="space-y-3 pt-2 border-t border-slate-100 dark:border-slate-800">
                      <p className="font-semibold text-sm pt-2">見積依頼フォーム</p>
                      <Input placeholder="お名前 *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="quote-form-name" />
                      <Input placeholder="会社名" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="quote-form-company" />
                      <Input type="email" placeholder="メールアドレス *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="quote-form-email" />
                      <Input placeholder="電話番号" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="quote-form-phone" />
                      <textarea
                        placeholder="ご要望・ご質問など"
                        value={form.message}
                        onChange={(e) => setForm({ ...form, message: e.target.value })}
                        rows={3}
                        data-testid="quote-form-message"
                        className="w-full rounded-md border border-slate-300 dark:border-slate-700 bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                      <Button onClick={submitRequest} disabled={submitting} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="quote-form-submit">
                        {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                        見積を依頼する
                      </Button>
                    </div>
                  )}
                  <p className="text-xs text-slate-400 mt-4">
                    ※ 表示金額は概算です。正式なお見積は担当者よりご案内します。
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}
